import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { Info } from 'lucide-react';
import { previousAnalyticsPeriod } from '@/components/shared/AnalyticsDateRangePicker';
import { Tooltip } from '@/components/ui/tooltip';

export const AI_REFERRAL_SHARE_HELP =
  'AI referral share is AI or chatbot sessions divided by all sessions to this page for the selected date range. The delta is the percentage-point change versus the preceding equal-length period.';

export interface AnalyticsPeriodComparison {
  current: unknown;
  previous: unknown;
}

export interface AnalyticsComparisonRange {
  startDate: string;
  endDate: string;
}

export interface AnalyticsComparisonRanges {
  current: AnalyticsComparisonRange;
  previous: AnalyticsComparisonRange;
}

export interface AnalyticsRowComparison<T> {
  current: Map<string, T>;
  previous: Map<string, T>;
}

function numericValue(value: unknown): number {
  return optionalNumericValue(value) ?? 0;
}

function optionalNumericValue(value: unknown): number | null {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (typeof value !== 'number' && typeof value !== 'string')
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getAnalyticsComparisonRanges(
  startDate: string,
  endDate: string
): AnalyticsComparisonRanges {
  return {
    current: {
      startDate,
      endDate,
    },
    previous: previousAnalyticsPeriod(startDate, endDate),
  };
}

export function buildAnalyticsRowComparison<T>(
  currentRows: T[] | null | undefined,
  previousRows: T[] | null | undefined,
  getKey: (row: T) => string
): AnalyticsRowComparison<T> {
  const indexRows = (rows: T[] | null | undefined) => {
    const index = new Map<string, T>();
    (rows || []).forEach(row => {
      const key = getKey(row);
      if (key) index.set(key, row);
    });
    return index;
  };

  return {
    current: indexRows(currentRows),
    previous: indexRows(previousRows),
  };
}

export function analyticsRowMetric<T>(
  comparison: AnalyticsRowComparison<T>,
  key: string,
  getValue: (row: T) => unknown
): AnalyticsPeriodComparison | undefined {
  const currentRow = comparison.current.get(key);
  const previousRow = comparison.previous.get(key);
  if (!currentRow || !previousRow) return undefined;
  const current = optionalNumericValue(getValue(currentRow));
  const previous = optionalNumericValue(getValue(previousRow));
  if (current === null || previous === null) return undefined;
  return {
    current,
    previous,
  };
}

type AnalyticsDeltaMode = 'percent' | 'points' | 'absolute';

function deltaPresentation(
  comparison: AnalyticsPeriodComparison,
  mode: AnalyticsDeltaMode
): {
  label: string;
  direction: 'up' | 'down' | 'flat';
  description: string;
} | null {
  const current = optionalNumericValue(comparison.current);
  const previous = optionalNumericValue(comparison.previous);
  if (current === null || previous === null) return null;
  const difference = current - previous;

  if (difference === 0) {
    return {
      label: mode === 'points' ? '0.0pp' : mode === 'absolute' ? '0.0' : '0%',
      direction: 'flat',
      description: 'No change versus the previous period',
    };
  }

  const direction = difference > 0 ? 'up' : 'down';
  if (mode === 'points') {
    const scale = Math.max(Math.abs(current), Math.abs(previous)) <= 1 ? 100 : 1;
    const points = difference * scale;
    return {
      label: `${Math.abs(points).toFixed(1)}pp`,
      direction,
      description: `${Math.abs(points).toFixed(1)} percentage points ${
        direction === 'up' ? 'up' : 'down'
      } versus the previous period`,
    };
  }

  if (mode === 'absolute') {
    return {
      label: Math.abs(difference).toFixed(1),
      direction,
      description: `${Math.abs(difference).toFixed(1)} ${
        direction === 'up' ? 'up' : 'down'
      } versus the previous period`,
    };
  }

  if (previous === 0) {
    return {
      label: 'New',
      direction,
      description: 'New in this period',
    };
  }

  const percent = (difference / Math.abs(previous)) * 100;
  const rounded = Math.abs(percent) >= 100 ? Math.round(percent) : Number(percent.toFixed(1));
  return {
    label: `${Math.abs(rounded)}%`,
    direction,
    description: `${Math.abs(rounded)}% ${
      direction === 'up' ? 'up' : 'down'
    } versus the previous period`,
  };
}

export function AnalyticsMetricValue({
  value,
  comparison,
  deltaMode = 'percent',
  lowerIsBetter = false,
  emphasize = false,
  align = 'end',
  valueFontSize = '14px',
}: {
  value: string;
  comparison?: AnalyticsPeriodComparison;
  deltaMode?: AnalyticsDeltaMode;
  lowerIsBetter?: boolean;
  emphasize?: boolean;
  align?: 'start' | 'end';
  valueFontSize?: string;
}) {
  const delta = comparison ? deltaPresentation(comparison, deltaMode) : null;
  const favorable =
    delta?.direction === 'flat'
      ? null
      : lowerIsBetter
        ? delta?.direction === 'down'
        : delta?.direction === 'up';
  const color =
    favorable === null ? 'ui.text.tertiary' : favorable ? 'status.success.fg' : 'status.danger.fg';

  const showDelta = delta !== null;

  return (
    <VStack align={align} gap="0.5" minW={0}>
      <Text
        as="span"
        fontSize={valueFontSize}
        color="ui.text.primary"
        fontWeight={emphasize ? '750' : '600'}
        lineHeight="1.1"
        fontVariantNumeric="tabular-nums"
        whiteSpace="nowrap"
      >
        {value}
      </Text>
      {showDelta ? (
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          gap="1"
          color={color}
          fontSize="12px"
          fontWeight="650"
          lineHeight="1"
          fontVariantNumeric="tabular-nums"
          whiteSpace="nowrap"
          aria-label={delta.description}
          title={delta.description}
        >
          <Box as="span" aria-hidden="true">
            {delta.direction === 'up' ? '↑' : delta.direction === 'down' ? '↓' : '–'}
          </Box>
          <Box as="span">{delta.label}</Box>
        </Box>
      ) : comparison ? (
        <Box as="span" h="12px" aria-hidden="true" />
      ) : null}
    </VStack>
  );
}

export function AnalyticsPeriodLegend() {
  return (
    <HStack
      as="span"
      h="26px"
      px="2.5"
      gap="1"
      color="ui.text.tertiary"
      bg="ui.surface.selected"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="ui.border.subtle"
      borderRadius="full"
      flexShrink={0}
      title="Selected date range compared with the preceding equal-length period"
    >
      <Text
        as="span"
        fontSize="11px"
        fontWeight="700"
        letterSpacing="0.06em"
        textTransform="uppercase"
        whiteSpace="nowrap"
      >
        vs prior
      </Text>
      <Box as="span" w="1px" h="12px" bg="ui.border.default" aria-hidden="true" />
      <Text
        as="span"
        fontSize="11px"
        fontWeight="650"
        letterSpacing="0.04em"
        textTransform="uppercase"
        whiteSpace="nowrap"
      >
        period
      </Text>
    </HStack>
  );
}

export function AnalyticsTableHeaderLabel({
  label,
  helpText,
  align = 'start',
}: {
  label: string;
  helpText?: string;
  align?: 'start' | 'end';
}) {
  return (
    <HStack
      gap="1"
      justify={align === 'end' ? 'flex-end' : 'flex-start'}
      minW={0}
      flexWrap="nowrap"
      whiteSpace="nowrap"
    >
      <Text
        fontSize="11px"
        lineHeight="1"
        color="ui.text.tertiary"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="0.05em"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
      {helpText ? (
        <Tooltip
          content={helpText}
          openDelay={200}
          showArrow
          contentProps={{
            layerStyle: 'surfacePopover',
            maxW: '320px',
            px: '3',
            py: '2.5',
            borderRadius: 'glassInput',
            color: 'ui.text.primary',
            fontSize: 'sm',
            lineHeight: '1.5',
          }}
        >
          <Box
            asChild
            w="16px"
            h="16px"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            borderRadius="5px"
            border="0"
            color="ui.text.tertiary"
            bg="transparent"
            cursor="help"
            _hover={{ color: 'ui.text.primary', bg: 'ui.surface.input' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'status.info.border',
              outlineOffset: '2px',
            }}
          >
            <button type="button" aria-label={`About ${label}`}>
              <Info size={13} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </Box>
        </Tooltip>
      ) : null}
    </HStack>
  );
}
